import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  ErrorCode,
  InvalidFormField,
  LiveRefused,
  PaymentsNotReady,
  StoreNotFound,
  type StoreRoleForbidden,
  StreamNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { isoDate, parse } from "../model/common";
import {
  type BreakPatch,
  type BreakPricingPatch,
  LiveBreakDetail,
  LiveBreakHost,
  LiveBreakHostDetail,
  LiveBreakSummary,
  LiveBroadcastCredentials,
  LiveCancelledBreak,
  LiveChatMessage,
  LiveCheckout,
  LiveCsv,
  LiveFinishedBreak,
  LiveIngestCredentials,
  LiveLabel,
  LiveOrder,
  LiveOrderRecording,
  LivePackingSlip,
  LiveParsedSpots,
  LivePromotedBreak,
  LiveQueueEntry,
  LiveRealtimeToken,
  LiveReveal,
  LiveSegmentsAdded,
  LiveShipmentAddressSaved,
  LiveShipmentsOpened,
  LiveSpotList,
  LiveSpotTemplate,
  LiveStage,
  LiveStoreShipping,
  LiveStream,
  LiveStreamDetail,
  LiveStreamFeed,
  LiveStreamHostDetail,
  LiveStreamRecording,
  LiveStreamViewers,
  LiveSyncRecording,
  LiveSyncStream,
  LiveVideoToken,
  type NewBreaks,
  type NewSegment,
  type NewSpotList,
  type NewStream,
  type ShippingAddress,
} from "../model/live";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

const StreamsResponse = z.object({ streams: z.array(LiveStream) });
const OrdersResponse = z.object({ orders: z.array(LiveOrder) });
const TemplatesResponse = z.object({ templates: z.array(LiveSpotTemplate) });
const QueueResponse = z.object({ breaks: z.array(LiveQueueEntry) });
const FinishedBreaksResponse = z.object({ breaks: z.array(LiveFinishedBreak), nextBefore: isoDate.nullable() });
const HostQueueResponse = z.object({ breaks: z.array(LiveBreakHost) });
const AddBreaksResponse = z.object({ breakIds: z.array(z.string()) });
const SpotListsResponse = z.object({ lists: z.array(LiveSpotList) });
const ChatResponse = z.object({ messages: z.array(LiveChatMessage) });
const ActionResponse = z.object({ ok: z.boolean() });

/**
 * Live breaks, both sides of the counter: the host's `/stores/:storeId/live/*`
 * and `/live/streams|breaks/*` routes, and the buyer's `/live/*`.
 *
 * Buyer-side refusals carry a code (`not_found`, `not_pending`, a sold-out
 * grid) and become `LiveRefused`; a shop whose Stripe account cannot take
 * money yet is `PaymentsNotReady`.
 */
export class LiveApi {
  constructor(private readonly t: Transport) {}

  /** Every stream a store has run or scheduled; with `loc_<id>` for `storeId`, only that location's. */
  async streams(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<LiveStream[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/live/streams`);
    return forward(
      r,
      (j) => parse(StreamsResponse, j).streams,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Opens a stream at one of the shop's locations, hosted by the caller, with
   * an empty line-up. A location already holding an open stream refuses with
   * `location_has_stream` and that stream's id in `details.streamId`.
   */
  async createStream(
    ctx: ApiContext,
    storeId: string,
    stream: NewStream,
  ): Promise<
    Result<LiveStream, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | LiveRefused>
  > {
    const r = await this.t.post(ctx, `/stores/${storeId}/live/streams`, {
      body: { ...stream, scheduledFor: stream.scheduledFor?.toISOString() },
    });
    return forward(
      r,
      (j) => parse(LiveStream, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue !== "") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /** DRAFT to ON_SALE. */
  async publishBreak(ctx: ApiContext, breakId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/breaks/${breakId}/publish`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "breakId") return new LiveRefused("not_found", e.detailedDesc, {}, e);
          if (e.incorrectValue === "") return new LiveRefused(e.code, e.detailedDesc, e.details, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** One break as a shared link previews it. Public: no session needed, and no spots in it. */
  async breakSummary(
    ctx: ApiContext,
    breakId: string,
  ): Promise<Result<LiveBreakSummary, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/breaks/${breakId}/summary`);
    return forward(
      r,
      (j) => parse(LiveBreakSummary, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "") return new LiveRefused(e.code, e.detailedDesc, e.details, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** One break's slot grid, as a buyer sees it. A DRAFT break does not exist to a buyer. */
  async break(ctx: ApiContext, breakId: string): Promise<Result<LiveBreakDetail, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/breaks/${breakId}`);
    return forward(
      r,
      (j) => parse(LiveBreakDetail, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "") return new LiveRefused(e.code, e.detailedDesc, e.details, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Claims slots and opens a payment for them. A hold is not a sale: the order
   * is PAID only once Stripe confirms. When `needsAddress` is true there is no
   * client secret yet; post one with `setShipping`.
   */
  async checkout(
    ctx: ApiContext,
    breakId: string,
    slotNumbers: number[],
  ): Promise<Result<LiveCheckout, ApiNotAuthorized | LiveRefused | PaymentsNotReady | InvalidFormField>> {
    const r = await this.t.post(ctx, `/live/breaks/${breakId}/checkout`, { body: { slotNumbers } });
    return forward(
      r,
      (j) => parse(LiveCheckout, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue !== "") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          if (e.code === "rejected") return new PaymentsNotReady(e.detailedDesc, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /** Gives the address for a held order, prices the postage, and returns the payment form. */
  async setShipping(
    ctx: ApiContext,
    orderId: string,
    address: ShippingAddress,
  ): Promise<Result<LiveCheckout, ApiNotAuthorized | LiveRefused | PaymentsNotReady | InvalidFormField>> {
    const r = await this.t.post(ctx, `/live/orders/${orderId}/shipping`, { body: address });
    return forward(
      r,
      (j) => parse(LiveCheckout, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue !== "") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          if (e.code === "rejected") return new PaymentsNotReady(e.detailedDesc, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /** Backs out of a checkout and frees the slots. */
  async release(ctx: ApiContext, orderId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/orders/${orderId}/release`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "") return new LiveRefused(e.code, e.detailedDesc, e.details, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The caller's own break orders, newest first. */
  async orders(ctx: ApiContext): Promise<Result<LiveOrder[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/live/orders");
    return forward(
      r,
      (j) => parse(OrdersResponse, j).orders,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** The caller's signed recording playback credential and purchased spot offsets. */
  async orderRecording(
    ctx: ApiContext,
    orderId: string,
    slotNumber?: number,
  ): Promise<Result<LiveOrderRecording, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(
      ctx,
      `/live/orders/${orderId}/recording${slotNumber === undefined ? "" : `?slotNumber=${slotNumber}`}`,
    );
    return forward(
      r,
      (j) => parse(LiveOrderRecording, j),
      (e) => {
        if (e instanceof ValidationError) {
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  // ─── The buyer's side ─────────────────────────────────────────────────────

  /**
   * What is on: live now, coming up, and recently finished.
   *
   * **Unauthenticated** — call it with `anonymous`. This is the front of the
   * shop: a link shared into a group chat, a shop's own website, someone who has
   * not opened an app yet. Everything in it is already public. Buying is where
   * the account starts mattering, and that is guarded where it happens.
   *
   * `storeId` narrows it to one shop; without it the feed is the whole platform.
   *
   * The bucketing exists because a single ordered query silently filled its
   * limit with finished streams, so `live-host.test.ts` asserts a scheduled stream
   * lands in `upcoming` and specifically *not* in `live`.
   */
  async streamFeed(
    ctx: ApiContext,
    opts: { storeId?: string } = {},
  ): Promise<Result<LiveStreamFeed, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/live/streams", { query: { storeId: opts.storeId } });
    return forward(
      r,
      (j) => parse(LiveStreamFeed, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** One stream and its break queue, with no session needed. DRAFT breaks are absent: they are still being built. */
  async stream(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveStreamDetail, ApiNotAuthorized | StreamNotFound>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}`);
    return forward(
      r,
      (j) => parse(LiveStreamDetail, j),
      (e) => {
        if (e instanceof ValidationError) return new StreamNotFound(streamId, e);
        return e;
      },
    );
  }

  /** Full stream recording timeline. Credentials are freshly signed per request. */
  async streamRecording(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<z.output<typeof LiveStreamRecording>, ApiNotAuthorized | StreamNotFound | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/recording`);
    return forward(
      r,
      (j) => parse(LiveStreamRecording, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "streamId") return new StreamNotFound(streamId, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  // ─── The host's console ───────────────────────────────────────────────────
  //
  // Every route below is behind a store-ownership check. They refuse with a
  // code rather than a field name — a break that is already randomized, a stream
  // that has not ended — so they map to `LiveRefused`, whose `code` is what a
  // caller branches on and whose `reason` is the sentence to show.

  /** Breaks selling now, fullest first: every shop's, or one shop's. Public. */
  async queue(ctx: ApiContext, opts: { storeId?: string } = {}): Promise<Result<LiveQueueEntry[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/live/queue", { query: opts.storeId ? { storeId: opts.storeId } : {} });
    return forward(
      r,
      (j) => parse(QueueResponse, j).breaks,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Breaks already ripped, newest first, a page at a time. */
  async finishedBreaks(
    ctx: ApiContext,
    opts: { storeId?: string; before?: Date } = {},
  ): Promise<Result<{ breaks: LiveFinishedBreak[]; nextBefore: Date | null }, ApiNotAuthorized>> {
    const query: Record<string, string> = {};
    if (opts.storeId) query.storeId = opts.storeId;
    if (opts.before) query.before = opts.before.toISOString();
    const r = await this.t.get(ctx, "/live/finished", { query });
    return forward(
      r,
      (j) => parse(FinishedBreaksResponse, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** The shop's queue as the host sees it: every unfinished break, fullest first, with the economics. */
  async hostQueue(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<LiveBreakHost[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/live/queue`);
    return forward(
      r,
      (j) => parse(HostQueueResponse, j).breaks,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Puts a box on sale in the shop's queue, with no stream. */
  async addBreaks(
    ctx: ApiContext,
    storeId: string,
    breaks: NewBreaks,
  ): Promise<Result<string[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | LiveRefused>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/live/queue`, {
      body: { ...breaks, breakNotBefore: breaks.breakNotBefore?.toISOString() },
    });
    return forward(
      r,
      (j) => parse(AddBreaksResponse, j).breakIds,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue !== "") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /** Ready-made label pools: the teams in a league, in a fixed order. */
  async spotTemplates(ctx: ApiContext): Promise<Result<LiveSpotTemplate[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/live/templates");
    return forward(
      r,
      (j) => parse(TemplatesResponse, j).templates,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** The lists a host can build a break from: built-in leagues, the curated library, the shop.s own. */
  async spotLists(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<LiveSpotList[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/spot-lists`);
    return forward(
      r,
      (j) => parse(SpotListsResponse, j).lists,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Keeps a list for next time. */
  async saveSpotList(
    ctx: ApiContext,
    storeId: string,
    list: NewSpotList,
  ): Promise<
    Result<LiveSpotList, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField | LiveRefused>
  > {
    const r = await this.t.post(ctx, `/stores/${storeId}/spot-lists`, { body: list });
    return forward(
      r,
      (j) => parse(LiveSpotList, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue !== "") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /** Removes one of the shop.s own lists. Breaks already built from it keep their spots. */
  async deleteSpotList(
    ctx: ApiContext,
    storeId: string,
    spotListId: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | LiveRefused>> {
    const r = await this.t.delete(ctx, `/stores/${storeId}/spot-lists/${spotListId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /**
   * Reads a pasted spot list — tab-separated, CSV, or one name per line — and
   * answers what it made of it, warnings and all.
   *
   * Server-side on purpose: a host pasting three hundred rows needs to be told
   * that line 41 had no name before the stream rather than during it.
   */
  async parseSpots(
    ctx: ApiContext,
    text: string,
  ): Promise<Result<LiveParsedSpots, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/live/spots/parse", { body: { text } });
    return forward(
      r,
      (j) => parse(LiveParsedSpots, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  /** The host's view of a stream: its breaks with the economics attached. */
  async streamHostDetail(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveStreamHostDetail, ApiNotAuthorized | StreamNotFound>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/host`);
    return forward(
      r,
      (j) => parse(LiveStreamHostDetail, j),
      (e) => {
        if (e instanceof ValidationError) return new StreamNotFound(streamId, e);
        return e;
      },
    );
  }

  /** Who is in the room right now, with the spots each has bought. Host only. */
  async streamViewers(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveStreamViewers, ApiNotAuthorized | StreamNotFound>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/viewers`);
    return forward(
      r,
      (j) => parse(LiveStreamViewers, j),
      (e) => {
        if (e instanceof ValidationError) return new StreamNotFound(streamId, e);
        return e;
      },
    );
  }

  /** The host's view of one break: the economics and every buyer. */
  async breakHostDetail(
    ctx: ApiContext,
    breakId: string,
  ): Promise<Result<LiveBreakHostDetail, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/breaks/${breakId}/host`);
    return forward(
      r,
      (j) => parse(LiveBreakHostDetail, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Edits a break's title, tile image or break time; answers the break read back. Refused once it is over. */
  async updateBreak(
    ctx: ApiContext,
    breakId: string,
    patch: BreakPatch,
  ): Promise<Result<LiveBreakHostDetail, ApiNotAuthorized | LiveRefused>> {
    const { breakNotBefore, ...rest } = patch;
    const r = await this.t.patch(ctx, `/live/breaks/${breakId}`, {
      body: {
        ...rest,
        ...(breakNotBefore === undefined ? {} : { breakNotBefore: breakNotBefore?.toISOString() ?? null }),
      },
    });
    return forward(
      r,
      (j) => parse(LiveBreakHostDetail, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Mints publish credentials so the host can broadcast from the browser.
   *
   * The token is short-lived and is never persisted — call this again rather
   * than keeping one.
   */
  async provisionStream(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveBroadcastCredentials, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stream`);
    return forward(
      r,
      (j) => parse(LiveBroadcastCredentials, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** RTMP details for a host broadcasting from OBS or a hardware encoder. */
  async provisionIngest(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveIngestCredentials, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/ingest`);
    return forward(
      r,
      (j) => parse(LiveIngestCredentials, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Ends the stream.
   *
   * `finishRipping` also closes any break that is mid-rip. A break still ON
   * SALE is never closed this way — that would strand paid buyers with no teams.
   */
  async endStream(
    ctx: ApiContext,
    streamId: string,
    opts: { finishRipping?: boolean } = {},
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/end`, { body: { finishRipping: opts.finishRipping } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Asks the video provider whether anything is actually being published, and
   * moves the stream out of SCHEDULED if so.
   *
   * The console polls this rather than trusting its own publish call: a host who
   * connected with the camera off is a different answer from one publishing
   * fine, and `reason` says which.
   */
  async syncStream(ctx: ApiContext, streamId: string): Promise<Result<LiveSyncStream, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/sync-stream`);
    return forward(
      r,
      (j) => parse(LiveSyncStream, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Asks the provider whether the recording has appeared, and releases the orders waiting on it. */
  async syncRecording(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveSyncRecording, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/sync-recording`);
    return forward(
      r,
      (j) => parse(LiveSyncRecording, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Adds to the line-up: queued boxes, or other segments. A box must be the
   * shop's, on sale, in no other line-up and held at this location. On a live
   * stream its spot holders are pushed at once.
   */
  async addSegments(
    ctx: ApiContext,
    streamId: string,
    items: NewSegment[],
  ): Promise<Result<LiveSegmentsAdded, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/segments`, {
      body: {
        items: items.map((i) => (i.kind === "BREAK" ? i : { ...i, scheduledFor: i.scheduledFor?.toISOString() })),
      },
    });
    return forward(
      r,
      (j) => parse(LiveSegmentsAdded, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Takes a planned segment out; a box goes back to the queue, still selling. */
  async removeSegment(
    ctx: ApiContext,
    streamId: string,
    segmentId: string,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.delete(ctx, `/live/streams/${streamId}/segments/${segmentId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Reorders the planned segments. Takes every planned one, exactly once. */
  async reorderSegments(
    ctx: ApiContext,
    streamId: string,
    orderedSegmentIds: string[],
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/segments/reorder`, { body: { orderedSegmentIds } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Puts a planned segment on screen, or clears it with `null`. A drawn box
   * holds the screen until finished (`break_in_progress`).
   */
  async setCurrentSegment(
    ctx: ApiContext,
    streamId: string,
    segmentId: string | null,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.put(ctx, `/live/streams/${streamId}/current`, { body: { segmentId } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Singles out one spot of the break on screen; `null` unpins it. */
  async pinSpot(
    ctx: ApiContext,
    streamId: string,
    slotId: string | null,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/pin-spot`, { body: { slotId } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Makes the caller the stream's host, for a shift change. */
  async takeOver(ctx: ApiContext, streamId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/takeover`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Changes what a break costs before it sells. Sold slots keep the price they
   * were bought at.
   *
   * Answers `{ ok: true }` and nothing more — read the break back with
   * `breakHostDetail` to see the new figures.
   */
  async setBreakPricing(
    ctx: ApiContext,
    breakId: string,
    pricing: BreakPricingPatch,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/breaks/${breakId}/pricing`, { body: pricing });
    return forward(
      r,
      (j) => {
        parse(ActionResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Runs the draw. **Once** — a break that has been randomized refuses a second
   * call, which is the property the whole product rests on.
   *
   * The reply carries the seed and algorithm so anyone can re-derive the result
   * from the commitment published before the draw.
   */
  async randomizeBreak(
    ctx: ApiContext,
    breakId: string,
    opts: { force?: boolean } = {},
  ): Promise<Result<LiveReveal, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/breaks/${breakId}/randomize`, {
      body: opts.force ? { force: true } : {},
    });
    return forward(
      r,
      (j) => parse(LiveReveal, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Closes a break that has been ripped. */
  async finishBreak(ctx: ApiContext, breakId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/breaks/${breakId}/finish`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Cancels a break and refunds every paid order in full. `refundsFailed` counts
   * the ones Stripe refused; those buyers are still owed.
   */
  async cancelBreak(
    ctx: ApiContext,
    breakId: string,
  ): Promise<Result<LiveCancelledBreak, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.delete(ctx, `/live/breaks/${breakId}`);
    return forward(
      r,
      (j) => parse(LiveCancelledBreak, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /**
   * Pushes the network that the break still has spots: once a day per break,
   * refused as `promoted_recently` (`details.nextPromoteAt`), `network_push_off`
   * or `not_on_sale`.
   */
  async promoteBreak(
    ctx: ApiContext,
    breakId: string,
  ): Promise<Result<LivePromotedBreak, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/breaks/${breakId}/promote`);
    return forward(
      r,
      (j) => parse(LivePromotedBreak, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Marks one spot as pulled on stream, so the viewers' grid follows the rip. */
  async markSpotOpened(ctx: ApiContext, slotId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/slots/${slotId}/opened`);
    return forward(
      r,
      (j) => {
        parse(ActionResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  // ─── Chat ─────────────────────────────────────────────────────────────────

  /** The room's recent messages. Both sides of the counter read this one. */
  async chat(ctx: ApiContext, streamId: string): Promise<Result<LiveChatMessage[], ApiNotAuthorized | StreamNotFound>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/chat`);
    return forward(
      r,
      (j) => parse(ChatResponse, j).messages,
      (e) => {
        if (e instanceof ValidationError) return new StreamNotFound(streamId, e);
        return e;
      },
    );
  }

  async postChatMessage(
    ctx: ApiContext,
    streamId: string,
    text: string,
  ): Promise<Result<LiveChatMessage, ApiNotAuthorized | StreamNotFound | InvalidFormField>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/chat`, { body: { text } });
    return forward(
      r,
      (j) => parse(LiveChatMessage, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new StreamNotFound(streamId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Hides a message. The host's moderation control; the row is kept. */
  async hideChatMessage(
    ctx: ApiContext,
    streamId: string,
    messageId: string,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.delete(ctx, `/live/streams/${streamId}/chat/${messageId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  // ─── Realtime and video ───────────────────────────────────────────────────

  /**
   * A short-lived credential for the realtime client.
   *
   * A null `token` means realtime is not configured in this environment. That is
   * **not** an error: the caller renders a grid that refreshes on its own.
   */
  async realtimeToken(
    ctx: ApiContext,
    streamId: string,
  ): Promise<Result<LiveRealtimeToken, ApiNotAuthorized | StreamNotFound>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/realtime-token`);
    return forward(
      r,
      (j) => parse(LiveRealtimeToken, j),
      (e) => {
        if (e instanceof ValidationError) return new StreamNotFound(streamId, e);
        return e;
      },
    );
  }

  /** A per-viewer token admitting one person to one room, to watch only. */
  async videoToken(ctx: ApiContext, streamId: string): Promise<Result<LiveVideoToken, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/video-token`);
    return forward(
      r,
      (j) => parse(LiveVideoToken, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  // ─── Stage ────────────────────────────────────────────────────────────────
  //
  // Buyers on the stream, voice only. A refusal (no paid spot, not live, the
  // stage full, an invitation gone) is a `LiveRefused` whose `code` names it.

  /** The stage queue and where the caller stands on it. */
  async stage(ctx: ApiContext, streamId: string): Promise<Result<LiveStage, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/streams/${streamId}/stage`);
    return forward(
      r,
      (j) => parse(LiveStage, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** A spot holder asks to come on. */
  async raiseHand(ctx: ApiContext, streamId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stage/raise`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Hand down, decline the invitation, or leave the stream: the server reads which. */
  async lowerHand(ctx: ApiContext, streamId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stage/lower`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Accepts the host's invitation; the microphone may publish once this succeeds. */
  async acceptStage(ctx: ApiContext, streamId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stage/accept`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Host: ask a buyer with a hand up to come on. */
  async inviteGuest(
    ctx: ApiContext,
    streamId: string,
    userId: string,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stage/invite`, { body: { userId } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Host: take a guest off, withdraw an invitation, or drop a hand. */
  async removeGuest(
    ctx: ApiContext,
    streamId: string,
    userId: string,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stage/remove`, { body: { userId } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Host: mute a guest. Only the guest can unmute. */
  async muteGuest(
    ctx: ApiContext,
    streamId: string,
    userId: string,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/streams/${streamId}/stage/mute`, { body: { userId } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  // ─── Shipping ─────────────────────────────────────────────────────────────
  //
  // Host-only, all of it. `sellerNote` and `buyerEmail` are private to the shop
  // and must never reach a buyer-facing screen.

  /** The Shipping tab: ready to ship, open parcels, refunds owed. */
  async storeShipping(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<LiveStoreShipping, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/shipping`);
    return forward(
      r,
      (j) => parse(LiveStoreShipping, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Opens parcels for these buyers' ready spots, or everyone's when omitted. Safe to repeat. */
  async openShipments(
    ctx: ApiContext,
    storeId: string,
    buyerUserIds?: string[],
  ): Promise<Result<LiveShipmentsOpened, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/shipping/open`, {
      body: buyerUserIds ? { buyerUserIds } : {},
    });
    return forward(
      r,
      (j) => parse(LiveShipmentsOpened, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The shipping export. The server names the file; the caller builds the Blob. */
  async shippingCsv(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<LiveCsv, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/shipping/csv`);
    return forward(
      r,
      (j) => parse(LiveCsv, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Sets the parcel's destination and re-quotes the postage.
   *
   * `messages` on the reply is what the carrier thought of the address and is
   * advisory only — the address is saved either way.
   */
  async setShipmentAddress(
    ctx: ApiContext,
    shipmentId: string,
    address: ShippingAddress,
  ): Promise<Result<LiveShipmentAddressSaved, ApiNotAuthorized | LiveRefused | InvalidFormField>> {
    const r = await this.t.put(ctx, `/live/shipments/${shipmentId}/address`, { body: address });
    return forward(
      r,
      (j) => parse(LiveShipmentAddressSaved, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue !== "") return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
          return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        }
        return e;
      },
    );
  }

  /** Buys the carrier label. Unavailable to a shop outside the US — see `fulfillment.carrierShipping`. */
  async buyShipmentLabel(
    ctx: ApiContext,
    shipmentId: string,
    senderPhone?: string,
  ): Promise<Result<LiveLabel, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/shipments/${shipmentId}/label`, {
      body: senderPhone ? { senderPhone } : {},
    });
    return forward(
      r,
      (j) => parse(LiveLabel, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Marks the parcel posted. The only way to close out a shipment with no carrier label. */
  async markShipmentShipped(
    ctx: ApiContext,
    shipmentId: string,
  ): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/live/shipments/${shipmentId}/shipped`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** What goes in the box, for a host printing it. */
  async packingSlip(
    ctx: ApiContext,
    shipmentId: string,
  ): Promise<Result<LivePackingSlip, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.get(ctx, `/live/shipments/${shipmentId}/packing-slip`);
    return forward(
      r,
      (j) => parse(LivePackingSlip, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }
}
